export interface FixedStepClock {
  accumulatorSeconds: number;
}

export interface FixedStepAdvance {
  clock: FixedStepClock;
  interpolationAlpha: number;
  stepsRun: number;
  droppedSeconds: number;
}

export function createFixedStepClock(): FixedStepClock {
  return { accumulatorSeconds: 0 };
}

const DEFAULT_FIXED_STEP_SECONDS = 1 / 60;
const DEFAULT_MAXIMUM_CATCH_UP_STEPS = 8;
const DEFAULT_MAXIMUM_FRAME_DELTA_SECONDS = 0.25;

/**
 * Advances an accumulator-driven fixed clock while keeping render timing
 * variable. `runStep` is invoked once per simulation step. The defaults are
 * structural safeguards kept here so `core/` has no project-layer imports; the
 * composition root may pass its configured timing values explicitly.
 */
export function advanceFixedStepClock(
  clock: Readonly<FixedStepClock>,
  frameDeltaSeconds: number,
  runStep: (fixedDeltaSeconds: number) => void,
  fixedStepSeconds = DEFAULT_FIXED_STEP_SECONDS,
  maximumCatchUpSteps = DEFAULT_MAXIMUM_CATCH_UP_STEPS,
): FixedStepAdvance {
  if (!Number.isFinite(frameDeltaSeconds) || frameDeltaSeconds < 0) {
    throw new RangeError("frameDeltaSeconds must be a finite, non-negative number");
  }
  if (!Number.isFinite(fixedStepSeconds) || fixedStepSeconds <= 0) {
    throw new RangeError("fixedStepSeconds must be a finite, positive number");
  }
  if (!Number.isInteger(maximumCatchUpSteps) || maximumCatchUpSteps < 1) {
    throw new RangeError("maximumCatchUpSteps must be a positive integer");
  }

  const acceptedFrameDelta = Math.min(
    frameDeltaSeconds,
    DEFAULT_MAXIMUM_FRAME_DELTA_SECONDS,
  );
  let droppedSeconds = frameDeltaSeconds - acceptedFrameDelta;
  let accumulatorSeconds = clock.accumulatorSeconds + acceptedFrameDelta;
  let stepsRun = 0;

  while (
    accumulatorSeconds >= fixedStepSeconds &&
    stepsRun < maximumCatchUpSteps
  ) {
    runStep(fixedStepSeconds);
    accumulatorSeconds -= fixedStepSeconds;
    stepsRun += 1;
  }

  if (accumulatorSeconds >= fixedStepSeconds) {
    const retainedRemainder = accumulatorSeconds % fixedStepSeconds;
    droppedSeconds += accumulatorSeconds - retainedRemainder;
    accumulatorSeconds = retainedRemainder;
  }

  return {
    clock: { accumulatorSeconds },
    interpolationAlpha: accumulatorSeconds / fixedStepSeconds,
    stepsRun,
    droppedSeconds,
  };
}
